import { Replay } from '@sentry/browser'
import { create as createJss } from 'jss'
import preset from 'jss-preset-default'
import queryString from 'query-string'
import React from 'react'
import { JssProvider } from 'react-jss'
import { Config } from '@customTypes/Config'
import { AppMode } from '@customTypes/Generic'
import App from './App'
import fixBooleanValues from './helpers/fixBooleanValues'
import prefixStyles from './jssPlugins/prefixStyles'
import { setEnvironment } from './lib/utils'
import { VisibilityProvider } from './react/context/VisibilityContext'
import { updateBaseUrl } from './services/api/apiClients'
import * as serviceWorker from './serviceWorker'
import { initializeSentry } from './utils/initializeSentry'
import normalizeLanguages from './utils/normalizeLanguages'
import { setParamsToLocalStorage } from './utils/setParamsToLocalStorage'
import { createRoot, Root } from 'react-dom/client'
import { SourceWebsiteType } from './lib'
import { SurveyProvider } from './react/context/SurveyContext'

declare global {
  interface Window {
    availyEmbedded: any
    availy: any
    availyModule: any
    dataLayer: any
    initAvaily: any
    availyDataLayer: any
    replayInstance: Replay | undefined
    debuggingData: any
  }
}

export const defaultConfig: Config = {
  clinicId: '',
  successUrl: '',
  asModal: false,
  elementId: '',
  embedded: false,
  mode: AppMode.NORMAL,
  lang: 'de',
  sourceWebsiteType: SourceWebsiteType.AVAILY_OTHER,
  brandColor: null,
}

const jss = createJss()
jss.setup(preset())
jss.use(prefixStyles())

let root: Root | undefined = undefined
export class Availy {
  element: string
  /**
   * @param element
   * @param args object which can contain clinicId
   */
  constructor(element: string, args: Partial<Config>) {
    if (!element) throw Error("Id of html element must be given to initialize the calendar. E.g.: new Calendar('root')")

    this.element = element

    let availyWrapperElement = document.getElementById(this.element)
    // eslint-disable-next-line no-restricted-globals
    const urlParams = fixBooleanValues<queryString.ParsedQuery<string>>(queryString.parse(location.search))

    // We first load what was configured when initializing Availy and then overwrite matching keys
    // with what was sent over as url params
    const config = {
      ...defaultConfig,
      ...args,
      ...urlParams,
      asModal: urlParams.asModal || args.asModal,
      displayHeader: urlParams.mode !== AppMode.CANCEL, // Do not display header for online cancellation
    }

    const elementLanguage = availyWrapperElement?.getAttribute('data-lang')
    const pageLanguage = document.documentElement.lang

    const language = normalizeLanguages(config.lang || pageLanguage || elementLanguage || 'de')

    const embedded = urlParams.embedded === 'true' || urlParams.embedded === true || args.embedded
    window.availyEmbedded = embedded
    config.embedded = embedded

    if (config.asModal && !args.skipCreatingNewModalContainer) {
      element = element + '-modal'

      const modalWrapper = document.createElement('div')
      modalWrapper.id = element

      document.body.appendChild(modalWrapper)

      availyWrapperElement = modalWrapper
    }

    if (args.env) {
      setEnvironment(args.env)
      updateBaseUrl(args.env)
    }

    initializeSentry(args.clinicId, args.successUrl)

    setParamsToLocalStorage()

    root = createRoot(availyWrapperElement!)
    root.render(
      <React.StrictMode>
        <VisibilityProvider>
          <JssProvider jss={jss}>
            <SurveyProvider>
              <App
                clinicId={config.clinicId}
                successUrl={config.successUrl}
                asModal={config.asModal}
                elementId={element}
                embedded={config.embedded}
                mode={config.mode}
                sourceWebsite={config.sourceWebsite}
                lang={language}
                treatmentBundleTypeReferenceId={config.treatmentBundleTypeReferenceId}
                displayHeader={config.displayHeader}
                sourceWebsiteType={config.sourceWebsiteType}
                brandColor={config.brandColor}
                defaultToInternalTracking={config.defaultToInternalTracking}
              />
            </SurveyProvider>
          </JssProvider>
        </VisibilityProvider>
      </React.StrictMode>
    )

    serviceWorker.unregister()
  }
}

const unmountAvaily = () => root?.unmount()

window.availyModule = { Availy, unmountAvaily }
